import React, { lazy } from 'react';
import { Route, useLocation } from 'react-router-dom';
import PageRoute from '../../components/Routes/PageRoute';
import { UserCapabilityName } from '../../types/userCapabilityName';
import { usersAnalytics } from './types/usersAnalytics';
import { UserFormContextProvider } from './contexts/userFormContext';
import { useFeatureToggles } from '../../hooks/useFeatureToggles';
import { FeatureToggleName } from '../../config/featureToggles/featureToggles.types';

const Users = lazy(() => import(/* webpackChunkName: "users" */ './Users'));

// Legacy create flow — used when the PAM toggle is OFF.
const UserCreateOld = lazy(
  () => import(/* webpackChunkName: "user-create-old" */ './UserCreateOld')
);

// New PAM "Add New Customer User" flow — used when the PAM toggle is ON.
const UserCreate = lazy(
  () => import(/* webpackChunkName: "user-create" */ './UserCreate')
);

/**
 * Toggles between the new PAM create flow and the legacy create flow based on
 * the PAM feature toggle. This is the single entry point for `users/create`;
 * everything downstream of the new flow is therefore gated behind PAM too.
 *
 * Internal employees always take the legacy flow. The PAM wizard is the customer
 * flow end to end — it assigns a single client organization, then walks accounts,
 * permission presets and extended access, all of which are org-scoped SoT
 * concepts that do not apply to a Charter employee. It also has no field for
 * anything an internal user needs (Charter user ID, title, manager, division) and
 * no access-policy step. Sending internal creation there landed the operator in
 * the customer flow with `isInternalUser` quietly ignored, so the only way to
 * create an employee was to notice the User Type radio and switch it back.
 */
const UserCreateToggle = () => {
  const { isFeatureToggledOn } = useFeatureToggles();
  const { search } = useLocation();
  const isPAMEnabled = isFeatureToggledOn(FeatureToggleName.PAM);
  const isInternalUser =
    new URLSearchParams(search).get('isInternalUser') === 'true';

  return isPAMEnabled && !isInternalUser ? (
    <UserCreate />
  ) : (
    <UserFormContextProvider>
      <UserCreateOld />
    </UserFormContextProvider>
  );
};

const UserEdit = lazy(
  () => import(/* webpackChunkName: "user-edit" */ './UserEdit')
);

const UserSuccess = lazy(
  () => import(/* webpackChunkName: "user-success" */ './UserSuccess')
);

const UserViewPage = lazy(
  () => import(/* webpackChunkName: "user-view-page" */ './UserPage')
);

const UserEditDetails = lazy(
  () => import(/* webpackChunkName: "user-edit-details" */ './UserEditDetails')
);

const UserEditAccessPolicies = lazy(
  () =>
    import(
      /* webpackChunkName: "user-edit-access-policies" */ './UserEditAccessPolicies'
    )
);

// New PAM edit surfaces (accounts / permissions / extended access). Gated
// behind the PAM toggle inside the page.
const UserEditPamPage = lazy(
  () => import(/* webpackChunkName: "user-edit-pam" */ './UserEditPam')
);

const {
  usersAnalytic,
  userCreate,
  userEdit,
  userSummary,
  userSuccess,
  userViewPage,
  userEditDetails,
  userEditPolicies,
  userInviteStatus,
} = usersAnalytics;

const users = usersAnalytic;

export const usersRoutes = (
  <Route path="users/*">
    <Route
      path=""
      element={
        <PageRoute
          pageInfo={users}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.ViewUsers,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <Users />
        </PageRoute>
      }
    />
    <Route
      path=":accessPolicies"
      element={
        <PageRoute
          pageInfo={users}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.ViewUsers,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <Users />
        </PageRoute>
      }
    />
    <Route
      path="create/*"
      element={
        <PageRoute
          pageInfo={userCreate}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.CreateUser,
            UserCapabilityName.CreateInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserCreateToggle />
        </PageRoute>
      }
    />
    <Route
      path="success"
      element={
        <PageRoute
          pageInfo={userSuccess}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.CreateUser,
            UserCapabilityName.CreateInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserSuccess />
        </PageRoute>
      }
    />
    <Route
      path="edit/:userId"
      element={
        <PageRoute
          pageInfo={userEdit}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.EditUser,
            UserCapabilityName.EditInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserFormContextProvider>
            <UserEdit />
          </UserFormContextProvider>
        </PageRoute>
      }
    />
    <Route
      path="view/:userId"
      element={
        <PageRoute
          pageInfo={userViewPage}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.ViewUsers,
            UserCapabilityName.ViewInternalUsers,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserViewPage />
        </PageRoute>
      }
    />
    <Route
      path="edit/:userId/details"
      element={
        <PageRoute
          pageInfo={userEditDetails}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.EditUser,
            UserCapabilityName.EditInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserFormContextProvider>
            <UserEditDetails />
          </UserFormContextProvider>
        </PageRoute>
      }
    />
    <Route
      path="edit/:userId/access-policies"
      element={
        <PageRoute
          pageInfo={userEditPolicies}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.EditUser,
            UserCapabilityName.EditInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserFormContextProvider>
            <UserEditAccessPolicies />
          </UserFormContextProvider>
        </PageRoute>
      }
    />
    <Route
      path="edit/:userId/pam/:section"
      element={
        <PageRoute
          pageInfo={userEditPolicies}
          userCapabilities={[
            UserCapabilityName.SuperAdmin,
            UserCapabilityName.EditUser,
            UserCapabilityName.EditInternalUser,
            UserCapabilityName.EndUserAllAdmin,
          ]}
        >
          <UserEditPamPage />
        </PageRoute>
      }
    />
  </Route>
);
